import * as React from "react";
import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Label } from "@/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface BroadcastComposerProps {
  offeringId: string;
  confirmedCount: number;
  broadcastsLast24h: number;
  showExpiry?: boolean;
  onSend: (subject: string, body: string, expiresAt?: string) => Promise<void>;
}

export function BroadcastComposer({
  confirmedCount,
  broadcastsLast24h,
  showExpiry = false,
  onSend,
}: BroadcastComposerProps) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [expiry, setExpiry] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSend = async () => {
    if (!subject.trim() || !body.trim() || confirmedCount === 0) return;
    setLoading(true);
    try {
      const expiresAt = expiry ? new Date(expiry).toISOString() : undefined;
      await onSend(subject.trim(), body.trim(), expiresAt);
      setSubject("");
      setBody("");
      setExpiry("");
    } finally {
      setLoading(false);
    }
  };

  const sendDisabled = confirmedCount === 0 || loading;

  return (
    <div className="space-y-4">
      {broadcastsLast24h >= 3 && (
        <Alert>
          <AlertDescription>
            You've sent {broadcastsLast24h} broadcasts in the last 24h (soft cap reached). Further sends are still allowed.
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-1.5">
        <Label htmlFor="broadcast-subject">Subject</Label>
        <Input
          id="broadcast-subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value.slice(0, 200))}
          maxLength={200}
          required
          placeholder="Enter subject…"
          className="transition-colors duration-150"
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="broadcast-body">Message</Label>
        <Textarea
          id="broadcast-body"
          value={body}
          onChange={(e) => setBody(e.target.value.slice(0, 5000))}
          maxLength={5000}
          required
          rows={6}
          placeholder="Write your message…"
          className="transition-colors duration-150 resize-y"
        />
      </div>

      {showExpiry && (
        <div className="space-y-1.5">
          <Label htmlFor="broadcast-expiry">
            Expires on <span className="text-slate-400 font-normal">(optional — defaults to 30 days)</span>
          </Label>
          <Input
            id="broadcast-expiry"
            type="date"
            value={expiry}
            min={new Date().toISOString().slice(0, 10)}
            onChange={(e) => setExpiry(e.target.value)}
            className="transition-colors duration-150 w-48"
          />
        </div>
      )}

      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className={sendDisabled ? "cursor-not-allowed inline-block" : "inline-block"}>
              <Button
                onClick={handleSend}
                disabled={sendDisabled}
                className="cursor-pointer transition-colors duration-150 gap-2"
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                Send broadcast
              </Button>
            </span>
          </TooltipTrigger>
          {confirmedCount === 0 && (
            <TooltipContent>No confirmed students</TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}
